import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { listKeys, createKey, revokeKey, type ApiKey } from '../../api/apikeys';
import { DataTable, type Column } from '../common/DataTable';
import { StatusBadge } from '../common/StatusBadge';
import { Modal } from '../common/Modal';
import { ConfirmDialog } from '../common/ConfirmDialog';

interface ApiKeysPanelProps {
  projectId: string;
}

export function ApiKeysPanel({ projectId }: ApiKeysPanelProps) {
  const { getJwtToken } = useAuth();

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [newFullKey, setNewFullKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await listKeys(projectId, token);
      setKeys(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [projectId, getJwtToken]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDescription.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const result = await createKey(projectId, newDescription.trim(), token);
      setShowCreateModal(false);
      setNewDescription('');
      setNewFullKey(result.rawKey);
      await fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await revokeKey(projectId, revokeTarget.keyId, token);
      setRevokeTarget(null);
      await fetchKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
      setRevokeTarget(null);
    }
  };

  const handleCopy = async () => {
    if (!newFullKey) return;
    try {
      await navigator.clipboard.writeText(newFullKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select + copy
      const el = document.getElementById('full-key-display');
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
      }
    }
  };

  const getKeyAgeDays = (createdAt: string): number => {
    const created = new Date(createdAt);
    const now = new Date();
    return Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  };

  const columns: Column<ApiKey>[] = [
    { key: 'keyPrefix', header: 'Key Prefix', render: (k) => <code>{k.keyPrefix}...</code> },
    { key: 'description', header: 'Description' },
    {
      key: 'enabled',
      header: 'Status',
      render: (k) => <StatusBadge status={k.enabled ? 'Active' : 'Revoked'} />,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (k) => new Date(k.createdAt).toLocaleDateString(),
    },
    {
      key: 'keyId',
      header: 'Age',
      render: (k) => {
        const days = getKeyAgeDays(k.createdAt);
        if (days >= 180) {
          return (
            <span className="key-age-badge key-age-badge--danger" title="Consider rotating this key">
              {'\uD83D\uDD34'} {days} days old
            </span>
          );
        }
        if (days >= 90) {
          return (
            <span className="key-age-badge key-age-badge--warn" title="Consider rotating this key">
              {'\u26A0'} {days} days old
            </span>
          );
        }
        return (
          <span className="key-age-badge key-age-badge--ok">
            {days} days
          </span>
        );
      },
    },
    {
      key: 'lastUsedAt',
      header: 'Last Used',
      render: (k) => k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never',
    },
    {
      key: 'actions',
      header: '',
      render: (k) =>
        k.enabled ? (
          <button
            className="btn btn--danger btn--sm"
            onClick={(e) => { e.stopPropagation(); setRevokeTarget(k); }}
            type="button"
          >
            Revoke
          </button>
        ) : null,
    },
  ];

  return (
    <section className="view__section">
      <div className="view__header" style={{ marginBottom: 12 }}>
        <h2 className="view__section-title" style={{ margin: 0 }}>API Keys</h2>
        <button
          className="btn btn--primary btn--sm"
          onClick={() => setShowCreateModal(true)}
          type="button"
        >
          + Generate Key
        </button>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && keys.length === 0 ? (
        <div className="settings-card settings-card--empty">Loading keys...</div>
      ) : (
        <DataTable<ApiKey>
          columns={columns}
          data={keys}
          keyExtractor={(k) => k.keyId}
          emptyMessage="No API keys yet. Generate one to get started."
        />
      )}

      {/* Create key modal */}
      <Modal
        isOpen={showCreateModal && !newFullKey}
        onClose={() => { setShowCreateModal(false); setNewDescription(''); }}
        title="Generate API Key"
      >
        <form onSubmit={handleCreate}>
          <div className="modal__field">
            <label htmlFor="key-desc">Description</label>
            <input
              id="key-desc"
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Production key for service X"
              required
              autoFocus
            />
          </div>
          <div className="modal__actions">
            <button
              type="button"
              className="modal__cancel"
              onClick={() => { setShowCreateModal(false); setNewDescription(''); }}
            >
              Cancel
            </button>
            <button type="submit" className="modal__submit" disabled={creating}>
              {creating ? 'Generating...' : 'Generate Key'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Show full key modal — only closeable via the Done button */}
      <Modal
        isOpen={!!newFullKey}
        onClose={() => {}}
        title="API Key Created"
        wide
      >
        <div className="alert alert--warning" style={{ marginBottom: 16 }}>
          <strong>This key will not be shown again.</strong> Copy it now and store it securely.
        </div>
        <div className="key-display">
          <code id="full-key-display" className="key-display__value">{newFullKey}</code>
          <button
            className="btn btn--primary btn--sm"
            onClick={handleCopy}
            type="button"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <div className="modal__actions">
          <button
            type="button"
            className="modal__submit"
            onClick={() => { setNewFullKey(null); setShowCreateModal(false); setCopied(false); }}
          >
            Done
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!revokeTarget}
        title="Revoke API Key"
        message={`Revoke key "${revokeTarget?.keyPrefix}..."? This action cannot be undone.`}
        confirmLabel="Revoke"
        variant="danger"
        onConfirm={handleRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </section>
  );
}
